import React, { useRef, useState } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Redirect } from 'react-router-dom';

import {
  LayoutSideNavigation,
  Page,
  Button,
  H2
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import css from './StripeSubscriptions.module.css';
import { updateSubscriptionStartDateMaybe } from './utils';
import { getSubscriptionDetails } from '../../util/subscriptionsHelpers';

const Subscribe = ({ location, currentUser, onUpdateUserSubscriptionProfile }) => {
  const title = 'Subscriptions';
  const locationState = location?.state || {};
  const [clientSecret] = useState(locationState.clientSecret);
  const [subscriptionId] = useState(locationState.subscriptionId);
  const [priceId] = useState(locationState.priceId);
  const [userName] = useState(locationState.userName || '');
  const [userPrivateData] = useState(locationState.userPrivateData);
  const [name, setName] = useState(userName);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('error');
  const [paymentIntent, setPaymentIntent] = useState();
  const [updatedPrivateData, setUpdatedPrivateData] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [subscriptions1, setSubscriptions1] = useState(locationState.subscriptions || []);
  const isSubmittingRef = useRef(false);
  // subscription1 are the ones already on user, passed here

  const stripe = useStripe();
  const elements = useElements();

  if (!clientSecret || !subscriptionId || !priceId) {
    return <Redirect to={{ pathname: '/account/prices' }} />;
  }

  if (!stripe || !elements) {
    return '';
  }

  const persistSubscriptionToProfile = async () => {
    const subscriptions = [...subscriptions1];
    const alreadyExists = subscriptions.some(sub => {
      if (typeof sub === 'string') {
        return sub === priceId;
      }
      return sub?.priceId === priceId;
    });

    if (!alreadyExists) {
      subscriptions.push(getSubscriptionDetails(priceId, locationState.prices));
    }

    await updateSubscriptionStartDateMaybe(subscriptionId, priceId);

    await onUpdateUserSubscriptionProfile({
      privateData: {
        subscriptions,
      },
    });

    setSubscriptions1(subscriptions);
    setUpdatedPrivateData(true);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (isLoading || isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    setIsLoading(true);
    setMessage('');
    const cardElement = elements.getElement(CardElement);

    const privateData = userPrivateData;

    if (privateData && subscriptions1) {
      try {
        const existingIntentResp = await stripe.retrievePaymentIntent(clientSecret);
        const existingIntent = existingIntentResp?.paymentIntent;

        if (existingIntent?.status === 'succeeded') {
          setPaymentIntent(existingIntent);
          try {
            await persistSubscriptionToProfile();
            setMessageType('success');
            setMessage('Payment already succeeded. Redirecting to your subscriptions...');
          } catch (profileError) {
            setMessageType('error');
            setMessage('Payment succeeded, but we could not update your subscription profile. Open Subscriptions and refresh once.');
          }
          return;
        }

        const resp = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: name,
            },
          },
          setup_future_usage: 'off_session', // Saves the card
        });

        const error = resp.error;
        const paymentIntent = resp.paymentIntent;

        if (error) {
          const alreadySucceededPaymentIntent =
            error.code === 'payment_intent_unexpected_state' &&
            (error?.payment_intent?.status === 'succeeded' ||
              (error.message && error.message.includes('already succeeded')));

          if (alreadySucceededPaymentIntent) {
            setPaymentIntent(error.payment_intent || { status: 'succeeded' });
            try {
              await persistSubscriptionToProfile();
              setMessageType('success');
              setMessage('Payment already succeeded. Redirecting to your subscriptions...');
            } catch (profileError) {
              setMessageType('error');
              setMessage('Payment succeeded, but we could not update your subscription profile. Open Subscriptions and refresh once.');
            }
            return;
          }

          const missingPaymentIntent =
            error.code === 'resource_missing' ||
            (error.message && error.message.includes('No such payment_intent'));

          if (missingPaymentIntent) {
            setMessageType('error');
            setMessage('Your payment session expired. Please go back to Prices and select the plan again.');
          } else {
            setMessageType('error');
            setMessage(error.message);
          }
          setIsLoading(false);
          return;
        }

        setPaymentIntent(paymentIntent);

        if (paymentIntent && paymentIntent.status === 'succeeded') {
          try {
            await persistSubscriptionToProfile();
            setMessageType('success');
            setMessage('Payment successful. Redirecting to your subscriptions...');
          } catch (profileError) {
            setMessageType('error');
            setMessage('Payment succeeded, but we could not update your subscription profile. Open Subscriptions and refresh once.');
          }
        }
      } catch (e) {
        console.error('Error confirming card payment:', e);
        const alreadySucceededFromException =
          e?.code === 'payment_intent_unexpected_state' &&
          (e?.payment_intent?.status === 'succeeded' ||
            (e?.message && e.message.includes('already succeeded')));

        if (alreadySucceededFromException) {
          setPaymentIntent(e.payment_intent || { status: 'succeeded' });
          try {
            await persistSubscriptionToProfile();
            setMessageType('success');
            setMessage('Payment already succeeded. Redirecting to your subscriptions...');
          } catch (profileError) {
            setMessageType('error');
            setMessage('Payment succeeded, but we could not update your subscription profile. Open Subscriptions and refresh once.');
          }
        } else {
          setMessageType('error');
          setMessage('Something went wrong while confirming payment. Please try again or go back to Prices.');
        }
      } finally {
        isSubmittingRef.current = false;
        setIsLoading(false);
      }
    } else {
      setMessageType('error');
      setMessage('Missing customer profile data. Please return to Prices and start subscription again.');
      isSubmittingRef.current = false;
      setIsLoading(false);
    }
  };

  if (paymentIntent && paymentIntent.status === 'succeeded' && updatedPrivateData) {
    return <Redirect to={{ pathname: '/account/subscriptions' }} />;
  }

  return (
    <Page title={title}>

      <LayoutSideNavigation
        sideNavClassName={css.navigation}
        topbar={
          <TopbarContainer
            className={css.topbar}
            mobileRootClassName={css.mobileTopbar}
            desktopClassName={css.desktopTopbar}
            currentPage="StripeAccountSubscriptionsPage"
          />
        }
        sideNav={
          <>
            <H2 as="h1" className={css.title}>
              Subscriptions
            </H2>
          </>
        }
        footer={<FooterContainer />}
      >
        <div className={css.content}>
          <h1>Subscribe</h1>
          <br />
          <form onSubmit={handleSubmit}>
            <div className={css.formControl}>
              <label htmlFor="name">Full name</label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </div>
            <div style={{ width: '100%', height: '20px' }}></div>
            <div className={css.cardElementWrapper}>
              <CardElement />
            </div>
            <div style={{ width: '100%', height: '20px' }}></div>
            <Button disabled={isLoading}>{isLoading ? 'Processing...' : 'Subscribe'}</Button>
            {message ? (
              <div
                className={`${css.checkoutMessage} ${
                  messageType === 'success' ? css.checkoutMessageSuccess : css.checkoutMessageError
                }`}
                role="alert"
              >
                <div>{message}</div>
                <div className={css.checkoutMessageActions}>
                  <Link to="/account/prices">Back to Prices</Link>
                  <Link to="/account/subscriptions">Go to Subscriptions</Link>
                </div>
              </div>
            ) : null}
          </form>
        </div>
      </LayoutSideNavigation>
    </Page>
  );
};

export default withRouter(Subscribe);